import { destination, findRunwayEnd, NM_TO_M, toTrueDeg, wrap180 } from '@atc/core';
import { COMMAND_CATALOG, newRequest } from '@atc/sim';
import type { Lesson } from './lesson';

/** Every catalog entry opens a runnable exercise with state-based completion checks. */
export const PILOT_PRACTICE: readonly Lesson[] = COMMAND_CATALOG.map((entry): Lesson => ({
  id: `practice-${entry.id}`, title: entry.title, summary: entry.when, airport: 'llbg', realism: 'low',
  position: entry.id === 'contactTower' ? 'approach' : 'legacy',
  // Drills open straight onto the scope: they are one instruction each, and the reference page the
  // player arrived from has already said what the instruction is for.
  tier: 'beginner',
  commands: [entry.id],
  briefing: {
    chartIds: [],
    situation: 'Ben Gurion approach · single aircraft',
    scene: [entry.when],
    objectives: [entry.title],
    notes: [`Say: "${entry.spoken}". Type: ${entry.syntax}.`],
  },
  build(airport) {
    const cs = 'ELY123';
    const runway = findRunwayEnd(airport, airport.arrivalRunway)!;
    const p = destination(runway.lat, runway.lon, runway.headingDeg + 180, 10 * NM_TO_M);
    const fix = airport.fixes.has('GODED') ? 'GODED' : [...airport.fixes.values()].find((f) => !f.hidden)!.name;
    const ground = ['takeoff', 'lineUp', 'standby', 'contact'].includes(entry.id);
    const command = `${cs} ${entry.syntax.replace('GODED', fix).replace(/^(I|L) 12$/, `$1 ${runway.name}`)}`;
    return {
      setup(sim) {
        const ac = ground ? sim.addAircraft({ kind: 'departure', callsign: cs, type: 'B738', requested: '' }) : sim.addAircraft({ kind: 'arrival', callsign: cs, type: 'B738', ...p, altitudeFt: airport.elevationFt + 2500, headingDeg: runway.headingDeg, iasKt: 210, route: [fix] });
        if (ground) sim.command(`${cs} C 5`);
        else sim.command(`${cs} C 4 S 210`);
        if (entry.id === 'abort') sim.command(`${cs} I ${runway.name}`);
        if (entry.id === 'contactTower') { sim.command(`${cs} I ${runway.name}`); for (let i = 0; i < 200 && !ac.approach?.established; i++) sim.step(.25); }
        if (entry.id === 'resumeNav') sim.command(`${cs} C 270`);
        if (entry.id === 'standby') ac.pilot.request = newRequest('departure', sim.simTimeS);
        if (entry.id === 'contact') { sim.command(`${cs} T`); for (let i = 0; i < 1200 && ac.altitudeFt < airport.elevationFt + 2500; i++) sim.step(.25); }
      },
      steps: [{
        title: entry.title,
        text: `${entry.when} Address ${cs}. Try “El Al one two three, ${entry.spoken.replace('GODED', fix).replace('runway 12', `runway ${runway.name}`)}”, or use the typed shortcut below. Read the pilot response and watch the aircraft.`,
        hint: command,
        say: `El Al one two three, ${entry.spoken.replace('GODED', fix).replace('runway 12', `runway ${runway.name}`)}`,
        solution: [command], testTimeoutS: 300,
        check({ sim, events, radio }) {
          const a = sim.find(cs);
          // Radio only: acknowledgements and questions change no flight state, so the log is the check.
          const replied = radio.some((r) => r.callsign === cs && r.from === 'pilot');
          if (entry.category === 'Questions') return replied && events.some((e) => e.type === 'pilotQuery' && e.subject === entry.subject) ? 'pass' : null;
          if (entry.id === 'repeat' || entry.id === 'acknowledge') return replied ? 'pass' : null;
          // The standby counter, not the deferral: a request the pilot raised and auto-deferred is
          // not something the player said, and must not pass the drill on their behalf.
          if (entry.id === 'standby') return replied && (a?.pilot.request?.standbys ?? 0) > 0 ? 'pass' : null;
          if (entry.id === 'contact') return events.some((e) => e.type === 'handoff') ? 'pass' : null;
          if (entry.id === 'contactTower') return events.some((e) => e.type === 'academyTowerHandoff') ? 'pass' : null;
          if (!a) return { fail: 'The aircraft left your frequency. Restart the practice.' };
          // Heading 270 as said is magnetic; the aircraft flies it true.
          const done = entry.id === 'heading' ? Math.abs(wrap180(a.headingDeg - toTrueDeg(airport, 270))) < 2
            : entry.id === 'altitude' ? Math.abs(a.altitudeFt - 4000) < 50
            : entry.id === 'speed' ? Math.abs(a.iasKt - 180) < 3
            : entry.id === 'direct' ? a.nav.mode === 'direct' && a.nav.fix === fix
            : entry.id === 'hold' ? a.nav.mode === 'hold' && a.nav.hold?.fix === fix
            : entry.id === 'approach' ? !!a.approach && !a.approach.cleared
            : entry.id === 'land' ? !!a.approach?.cleared
            : entry.id === 'lineUp' ? a.phase === 'linedUp'
            : entry.id === 'takeoff' ? !a.onGround && a.altitudeFt > airport.elevationFt + 100
            : entry.id === 'abort' ? events.some((e) => e.type === 'goAround') && a.targetAltitudeFt > airport.elevationFt
            : entry.id === 'resumeNav' ? a.nav.mode === 'route' : false;
          return done && replied ? 'pass' : null;
        },
      }],
    };
  },
}));
